/**
 * TextEffects.ts — Animated text display effects.
 *
 * Provides typewriter text, flash/pulse alerts, "press any key" prompt,
 * escape menu (Resume/Save/Quit), rich text rendering, and paragraph skip.
 * All effects are skippable — pressing any key jumps to the end state.
 *
 * Shared across ChoiceRealms titles (Ascend, Gallowcrest).
 */

import type { Terminal } from './Terminal';
import type { InputManager } from './InputManager';
import type { ColorName } from './ColorTheme';
import type { GameEngine } from '@core/engine/GameEngine';
import type { SettingsManager } from '@game/Settings';
import { theme, RESET } from './ColorTheme';

export class TextEffects {
  private terminal: Terminal;
  private inputManager: InputManager;
  private settings: SettingsManager | null = null;
  private engine: GameEngine | null = null;
  private skipRequested = false;

  constructor(terminal: Terminal, inputManager: InputManager) {
    this.terminal = terminal;
    this.inputManager = inputManager;
  }

  /** Wire the SettingsManager for consistent typewriter speed across all scenes. */
  setSettings(settings: SettingsManager): void {
    this.settings = settings;
  }

  /** Wire the GameEngine for escape menu save/quit access. */
  setEngine(engine: GameEngine): void {
    this.engine = engine;
  }

  /**
   * Resolve the effective typewriter speed.
   * Settings override caller-provided speed for consistency.
   */
  private getEffectiveSpeed(callerSpeed: number): number {
    if (this.settings) {
      return this.settings.getTypewriterSpeed();
    }
    return callerSpeed;
  }

  // ─── Paragraph Skip ──────────────────────────────────

  /** Request paragraph-level skip (called during typewriter animation). */
  requestSkip(): void {
    this.skipRequested = true;
  }

  /** Reset the skip flag (call at start/end of paragraph). */
  resetSkip(): void {
    this.skipRequested = false;
  }

  /** Check if skip was requested. */
  isSkipRequested(): boolean {
    return this.skipRequested;
  }

  // ─── Core Effects ────────────────────────────────────

  /**
   * Typewriter effect: reveal text character-by-character.
   * Press any key to skip and show the full text instantly.
   * Respects paragraph-level skip: if skipRequested, shows text instantly.
   */
  async typewriter(text: string, color?: ColorName, speed = 30): Promise<void> {
    const effectiveSpeed = this.getEffectiveSpeed(speed);

    // Instant mode or paragraph skip active: write all text immediately
    if (effectiveSpeed === 0 || this.skipRequested) {
      this.terminal.write(text, color);
      return;
    }

    return new Promise<void>((resolve) => {
      let index = 0;
      let skipped = false;
      let interval: ReturnType<typeof setInterval> | null = null;

      const previousMode = this.inputManager.getMode();
      this.inputManager.setMode('anyKey');

      const skipHandler = () => {
        if (skipped) return;
        skipped = true;
        if (interval) clearInterval(interval);

        // Mark paragraph skip so subsequent lines in the same paragraph are instant
        this.skipRequested = true;

        const remaining = text.slice(index);
        this.terminal.write(remaining, color);

        this.inputManager.off(skipHandler);
        this.inputManager.setMode(previousMode);
        resolve();
      };

      this.inputManager.on(skipHandler);

      interval = setInterval(() => {
        if (skipped || index >= text.length) {
          if (interval) clearInterval(interval);
          if (!skipped) {
            this.inputManager.off(skipHandler);
            this.inputManager.setMode(previousMode);
            resolve();
          }
          return;
        }

        this.terminal.write(text[index], color);
        index++;
      }, effectiveSpeed);
    });
  }

  /** Typewriter effect for a full line (adds newline at end). */
  async typewriterLine(text: string, color?: ColorName, speed = 30): Promise<void> {
    await this.typewriter(text, color, speed);
    this.terminal.blankLine();
  }

  /**
   * Typewriter with inline {color:text} markup.
   * Parses markup and renders each segment with its specified color.
   */
  async typewriterRich(text: string, defaultColor: ColorName = 'white', speed = 30): Promise<void> {
    const segments = this.parseRichSegments(text, defaultColor);
    for (const seg of segments) {
      await this.typewriter(seg.text, seg.color, speed);
    }
  }

  /**
   * Flash effect: blink text a specified number of times.
   */
  async flash(text: string, color: ColorName = 'red', count = 3, intervalMs = 300): Promise<void> {
    return new Promise<void>((resolve) => {
      let remaining = count * 2;
      let visible = true;

      const interval = setInterval(() => {
        this.terminal.writeRaw('\x1b[s');

        if (visible) {
          this.terminal.write(text, color);
        } else {
          this.terminal.write(' '.repeat(text.length));
        }

        this.terminal.writeRaw('\x1b[u');

        visible = !visible;
        remaining--;

        if (remaining <= 0) {
          clearInterval(interval);
          this.terminal.writeRaw('\x1b[s');
          this.terminal.write(text, color);
          this.terminal.writeRaw('\x1b[u');
          resolve();
        }
      }, intervalMs);
    });
  }

  /**
   * "Press any key to continue" prompt.
   * Optionally accepts a startCol for frame-aware positioning.
   * Intercepts ESC key to show escape menu (Resume/Save/Quit).
   */
  async pressAnyKey(
    prompt = '  Press any key to continue...',
    color: ColorName = 'frostSilver',
    startCol?: number,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!startCol) {
        this.terminal.blankLine();
      }
      this.terminal.write(prompt, color);

      const previousMode = this.inputManager.getMode();
      this.inputManager.setMode('anyKey');

      const handler = async (event: import('./InputManager').InputEvent) => {
        // Intercept ESC for escape menu when engine is wired
        if (event.key === 'Escape' && this.engine) {
          this.inputManager.off(handler);
          let action: 'resume' | 'quit';
          try {
            action = await this.showEscapeMenu();
          } catch (err) {
            console.error('Escape menu failed:', err);
            action = 'resume';
          }

          if (action === 'resume') {
            this.inputManager.setMode('anyKey');
            this.inputManager.on(handler);
            if (!startCol) {
              this.terminal.write(prompt, color);
            }
          } else {
            this.inputManager.setMode(previousMode);
            // Clear the prompt line before resolving
            this.terminal.writeRaw('\r\x1b[2K');
            resolve();
          }
          return;
        }

        this.inputManager.off(handler);
        this.inputManager.setMode(previousMode);
        // Clear the prompt line before resolving
        this.terminal.writeRaw('\r\x1b[2K');
        resolve();
      };

      this.inputManager.on(handler);
    });
  }

  /**
   * Show a loading spinner while a promise resolves.
   */
  async withLoading<T>(label: string, task: Promise<T>): Promise<T> {
    const spinChars = ['|', '/', '-', '\\'];
    let spinIdx = 0;

    this.terminal.write(`${label} ${spinChars[0]}`, 'frostSilver');

    const interval = setInterval(() => {
      spinIdx = (spinIdx + 1) % spinChars.length;
      this.terminal.writeRaw('\b');
      this.terminal.write(spinChars[spinIdx], 'frostSilver');
    }, 100);

    try {
      const result = await task;
      clearInterval(interval);
      this.terminal.writeRaw('\b');
      this.terminal.writeLine('\u2713', 'green');
      return result;
    } catch (err) {
      clearInterval(interval);
      this.terminal.writeRaw('\b');
      this.terminal.writeLine('\u2717', 'red');
      throw err;
    }
  }

  /** Delay for a specified duration (skippable with any key). */
  async delay(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      let resolved = false;

      const previousMode = this.inputManager.getMode();
      this.inputManager.setMode('anyKey');

      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          this.inputManager.off(handler);
          this.inputManager.setMode(previousMode);
          resolve();
        }
      }, ms);

      const handler = () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          this.inputManager.off(handler);
          this.inputManager.setMode(previousMode);
          resolve();
        }
      };

      this.inputManager.on(handler);
    });
  }

  // ─── Escape Menu ─────────────────────────────────────

  /**
   * Show an escape menu overlay: Resume / Save / Quit.
   * Returns the action chosen by the player.
   */
  private async showEscapeMenu(): Promise<'resume' | 'quit'> {
    // Save cursor position
    this.terminal.writeRaw('\x1b[s');

    const { rows, cols } = this.terminal.getSize();
    const menuWidth = 40;
    const menuStartCol = Math.floor((cols - menuWidth) / 2);
    const menuStartRow = Math.floor(rows / 2) - 2;
    const bc = theme.fg('frostBlue');
    const tc = theme.fg('icyWhite');

    // Draw menu box
    this.terminal.setCursor(menuStartRow, menuStartCol);
    this.terminal.writeRaw(`${bc}╔${'═'.repeat(menuWidth - 2)}╗${RESET}`);

    this.terminal.setCursor(menuStartRow + 1, menuStartCol);
    this.terminal.writeRaw(`${bc}║${RESET}`);
    const titleText = '  PAUSED';
    this.terminal.write(titleText, 'icyWhite');
    this.terminal.setCursor(menuStartRow + 1, menuStartCol + menuWidth - 1);
    this.terminal.writeRaw(`${bc}║${RESET}`);

    this.terminal.setCursor(menuStartRow + 2, menuStartCol);
    this.terminal.writeRaw(`${bc}╠${'─'.repeat(menuWidth - 2)}╣${RESET}`);

    const options = ['[1] Resume', '[2] Save', '[3] Quit to Title'];
    for (let i = 0; i < options.length; i++) {
      this.terminal.setCursor(menuStartRow + 3 + i, menuStartCol);
      this.terminal.writeRaw(`${bc}║${RESET}  ${tc}${options[i]}${' '.repeat(menuWidth - 4 - options[i].length)}${bc}║${RESET}`);
    }

    this.terminal.setCursor(menuStartRow + 3 + options.length, menuStartCol);
    this.terminal.writeRaw(`${bc}╚${'═'.repeat(menuWidth - 2)}╝${RESET}`);

    return new Promise<'resume' | 'quit'>((resolve) => {
      const previousMode = this.inputManager.getMode();
      this.inputManager.setMode('anyKey');

      const handler = (event: import('./InputManager').InputEvent) => {
        const key = event.key;
        if (key === '1' || key === 'Escape') {
          this.inputManager.off(handler);
          this.inputManager.setMode(previousMode);
          this.terminal.writeRaw('\x1b[u');
          resolve('resume');
        } else if (key === '2') {
          // Save to slot 0 (quick save)
          const ctx = this.engine?.getContext();
          if (ctx?.saveSystem && ctx?.gameState) {
            try {
              ctx.saveSystem.save(0, ctx.gameState);
            } catch (err) {
              console.warn('[EscapeMenu] Save failed:', err);
            }
            // Flash "Saved!" briefly
            this.terminal.setCursor(menuStartRow + 3 + options.length + 1, menuStartCol + 2);
            this.terminal.write('  Saved!', 'green');
            setTimeout(() => {
              this.inputManager.off(handler);
              this.inputManager.setMode(previousMode);
              this.terminal.writeRaw('\x1b[u');
              resolve('resume');
            }, 800);
          } else {
            this.inputManager.off(handler);
            this.inputManager.setMode(previousMode);
            this.terminal.writeRaw('\x1b[u');
            resolve('resume');
          }
        } else if (key === '3') {
          this.inputManager.off(handler);
          this.inputManager.setMode(previousMode);
          resolve('quit');
        }
      };

      this.inputManager.on(handler);
    });
  }

  // ─── Rich Text Parsing ──────────────────────────────

  /** Parse {color:text} markup into segments. */
  private parseRichSegments(text: string, defaultColor: ColorName): Array<{ color: ColorName; text: string }> {
    const segments: Array<{ color: ColorName; text: string }> = [];
    let remaining = text;

    while (remaining.length > 0) {
      const braceIdx = remaining.indexOf('{');
      if (braceIdx === -1) {
        segments.push({ color: defaultColor, text: remaining });
        break;
      }

      if (braceIdx > 0) {
        segments.push({ color: defaultColor, text: remaining.slice(0, braceIdx) });
      }

      const closeIdx = remaining.indexOf('}', braceIdx);
      if (closeIdx === -1) {
        segments.push({ color: defaultColor, text: remaining.slice(braceIdx) });
        break;
      }

      const inner = remaining.slice(braceIdx + 1, closeIdx);
      const colonIdx = inner.indexOf(':');
      if (colonIdx === -1) {
        segments.push({ color: defaultColor, text: remaining.slice(braceIdx, closeIdx + 1) });
      } else {
        const colorName = inner.slice(0, colonIdx) as ColorName;
        const colorText = inner.slice(colonIdx + 1);
        segments.push({ color: colorName, text: colorText });
      }

      remaining = remaining.slice(closeIdx + 1);
    }

    return segments;
  }
}
